import { useEffect, useRef, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { AuditHistory } from "@/components/audit/AuditHistory";
import { PitchArrivalTypeSettingsEditor } from "@/components/pitches/PitchArrivalTypeSettingsEditor";
import { RentalPeriodPicker } from "@/components/stammdaten/RentalPeriodPicker";
import { CategoryMarketingSection } from "@/pages/stammdaten/CategoryMarketingSection";
import { api } from "@/lib/apiClient";
import { effectiveArticlePrice } from "@/lib/articleConstants";
import { formatCurrency } from "@/lib/money";
import {
  normalizeRentalPeriod,
  rentalPeriodPriceLabel,
  type RentalPeriod,
} from "@/lib/rentalPeriod";
import { CategoryColorPicker } from "@/components/stammdaten/CategoryColorPicker";
import {
  arrivalSettingsFromApi,
  arrivalSettingsToApi,
  emptyArrivalTypeSettings,
  hasArrivalTypeRestrictions,
  type PitchArrivalTypeSettingsForm,
} from "@/lib/pitchUsageType";
import type { ArrivalTypeRecord, ArticlePrice, ChargeRule, PitchCategory } from "@/types/camping";

interface CategoryFormState {
  name: string;
  name_en: string;
  color: string;
  sort_order: string;
  is_active: boolean;
  rental_period: RentalPeriod;
  restrict_arrival_types: boolean;
  arrival_type_settings: PitchArrivalTypeSettingsForm;
}

interface TenantSettings {
  integrations?: {
    tourist_integration?: {
      enabled?: boolean;
    };
  };
}

function categoryToForm(
  cat: PitchCategory,
  arrivalTypes: ArrivalTypeRecord[],
): CategoryFormState {
  const restrict = hasArrivalTypeRestrictions(cat.arrival_type_settings);
  return {
    name: cat.name,
    name_en: cat.name_en ?? "",
    color: cat.color,
    sort_order: String(cat.sort_order),
    is_active: cat.is_active,
    rental_period: normalizeRentalPeriod(cat.rental_period),
    restrict_arrival_types: restrict,
    arrival_type_settings: restrict
      ? arrivalSettingsFromApi(cat.arrival_type_settings, arrivalTypes)
      : emptyArrivalTypeSettings(arrivalTypes),
  };
}

function FormSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{title}</h3>
        {hint ? <p className="mt-1 text-xs text-label-secondary">{hint}</p> : null}
      </div>
      <div className="space-y-3 rounded-xl border border-separator bg-fill-tertiary/30 p-4">{children}</div>
    </section>
  );
}

export interface EditCategoryModalProps {
  open: boolean;
  category: PitchCategory | null;
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
}

export function EditCategoryModal({
  open,
  category,
  onClose,
  onSaved,
  canEdit,
}: EditCategoryModalProps) {
  const [form, setForm] = useState<CategoryFormState | null>(null);
  const [arrivalTypes, setArrivalTypes] = useState<ArrivalTypeRecord[]>([]);
  const [touristIntegrationEnabled, setTouristIntegrationEnabled] = useState(false);
  const [pitchPriceNet, setPitchPriceNet] = useState<string | null | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const marketingSave = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get<ArrivalTypeRecord[]>("/api/v1/arrival-types?include_inactive=true"),
      api.get<TenantSettings>("/api/v1/settings"),
    ])
      .then(([types, settings]) => {
        setArrivalTypes(types);
        setTouristIntegrationEnabled(
          settings.integrations?.tourist_integration?.enabled ?? false,
        );
      })
      .catch(() => {
        setArrivalTypes([]);
        setTouristIntegrationEnabled(false);
      });
  }, [open]);

  useEffect(() => {
    if (!open || !category) return;
    setForm(categoryToForm(category, arrivalTypes));
    setError(null);
    setSubmitting(false);
  }, [open, category, arrivalTypes]);

  useEffect(() => {
    if (!open || !category) return;
    let cancelled = false;
    setPitchPriceNet(undefined);
    api
      .get<ChargeRule[]>("/api/v1/charge-rules")
      .then(async (rules) => {
        const matches = rules.filter(
          (rule) =>
            rule.is_active &&
            rule.rule_type === "pitch_category_night" &&
            rule.pitch_category_id === category.id,
        );
        const rule = matches.find((item) => !item.guest_type) ?? matches[0];
        if (!rule) {
          if (!cancelled) setPitchPriceNet(null);
          return;
        }
        const prices = await api.get<ArticlePrice[]>(`/api/v1/articles/${rule.article_id}/prices`);
        const today = new Date().toISOString().slice(0, 10);
        const effective = effectiveArticlePrice(prices, today);
        if (!cancelled) setPitchPriceNet(effective?.amount ?? null);
      })
      .catch(() => {
        if (!cancelled) setPitchPriceNet(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, category]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!category || !form || !canEdit) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.patch(`/api/v1/pitch-categories/${category.id}`, {
        name: form.name.trim(),
        name_en: form.name_en.trim() || null,
        color: form.color,
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active,
        rental_period: form.rental_period,
        ...(touristIntegrationEnabled
          ? {
              arrival_type_settings: arrivalSettingsToApi(
                form.arrival_type_settings,
                form.restrict_arrival_types,
              ),
            }
          : {}),
      });
      if (marketingSave.current) await marketingSave.current();
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  if (!category || !form) return null;

  const priceLabel = rentalPeriodPriceLabel(form.rental_period);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={category.name}
      description={canEdit ? "Kategorie bearbeiten" : "Kategoriedetails (nur Lesen)"}
      size="lg"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {canEdit ? "Abbrechen" : "Schließen"}
          </Button>
          {canEdit ? (
            <Button type="submit" form="pitch-category-form" loading={submitting}>
              Speichern
            </Button>
          ) : null}
        </div>
      }
    >
      <form id="pitch-category-form" onSubmit={(e) => void onSubmit(e)} className="space-y-5">
        <FormSection title="Grundlagen">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Name (Deutsch)
              <input
                required
                disabled={!canEdit}
                className="eh-input"
                value={form.name}
                onChange={(e) => setForm((f) => (f ? { ...f, name: e.target.value } : f))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Name (Englisch)
              <input
                disabled={!canEdit}
                className="eh-input"
                value={form.name_en}
                onChange={(e) => setForm((f) => (f ? { ...f, name_en: e.target.value } : f))}
                placeholder="Optional für englische Buchungsstrecke"
              />
            </label>
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_8rem] sm:items-end">
            <label className="flex flex-col gap-1 text-sm">
              Farbe
              <CategoryColorPicker
                value={form.color}
                disabled={!canEdit}
                onChange={(color) => setForm((f) => (f ? { ...f, color } : f))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Sortierung
              <input
                type="number"
                disabled={!canEdit}
                className="eh-input"
                value={form.sort_order}
                onChange={(e) => setForm((f) => (f ? { ...f, sort_order: e.target.value } : f))}
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              disabled={!canEdit}
              checked={form.is_active}
              onChange={(e) => setForm((f) => (f ? { ...f, is_active: e.target.checked } : f))}
            />
            Aktiv
          </label>
        </FormSection>

        <FormSection
          title="Vermietung"
          hint="Der hinterlegte Stellplatzpreis gilt für diesen Zeitraum. Touristenplätze bleiben bei einer Nacht, Dauerstellplätze zum Beispiel beim Jahr und Abstellplätze beim Halbjahr."
        >
          <RentalPeriodPicker
            value={form.rental_period}
            disabled={!canEdit}
            onChange={(rental_period) => setForm((f) => (f ? { ...f, rental_period } : f))}
          />
          <p className="text-sm text-label">
            {pitchPriceNet === undefined ? (
              <span className="text-label-secondary">Preis wird geladen…</span>
            ) : pitchPriceNet ? (
              <>
                Hinterlegter Preis:{" "}
                <span className="font-semibold">{formatCurrency(pitchPriceNet)}</span> netto {priceLabel}
              </>
            ) : (
              <span className="text-label-secondary">
                Noch kein Stellplatzpreis hinterlegt. Der Betrag kommt aus der Buchungszuordnung und
                wird dann {priceLabel} angezeigt.
              </span>
            )}
          </p>
          <p className="text-xs text-label-tertiary">
            Ein volles Jahr entspricht 365 Nächten, ein Halbjahr 180, ein Quartal 90, ein Monat 30
            und eine Woche 7. Kürzere Aufenthalte werden anteilig berechnet.
          </p>
        </FormSection>

        <CategoryMarketingSection
          categoryId={category.id}
          categoryName={category.name}
          canEdit={canEdit}
          active={open}
          saveHandle={marketingSave}
        />

        {touristIntegrationEnabled && arrivalTypes.length > 0 ? (
          <FormSection title="Anreisearten" hint="Nur relevant, wenn die Tourist-Anbindung aktiv ist.">
            <PitchArrivalTypeSettingsEditor
              arrivalTypes={arrivalTypes}
              restrictArrivalTypes={form.restrict_arrival_types}
              onRestrictChange={(restrict_arrival_types) =>
                setForm((f) =>
                  f
                    ? {
                        ...f,
                        restrict_arrival_types,
                        arrival_type_settings: restrict_arrival_types
                          ? f.arrival_type_settings
                          : emptyArrivalTypeSettings(arrivalTypes),
                      }
                    : f,
                )
              }
              value={form.arrival_type_settings}
              onChange={(arrival_type_settings) =>
                setForm((f) => (f ? { ...f, arrival_type_settings } : f))
              }
              disabled={!canEdit}
            />
          </FormSection>
        ) : null}

        <AuditHistory entityType="pitch_category" entityId={category.id} active={open} />
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </form>
    </Modal>
  );
}
